"use client";

import { motion } from "framer-motion";
import { useLang } from "@/contexts/language-context";
import { Mail, Phone, Send } from "lucide-react";

const BUYERS = [
  { initial: "M", name: "Maria González", email: "maria.g@gmail.com", phone: "(214) 555-0142", events: 4, hue: 320 },
  { initial: "J", name: "James Carter", email: "jcarter@yahoo.com", phone: "(214) 555-0207", events: 2, hue: 260 },
  { initial: "A", name: "Alex Rivera", email: "alex.rivera@me.com", phone: "(214) 555-0388", events: 7, hue: 30 },
  { initial: "D", name: "Daniela López", email: "dani.lopez@outlook.com", phone: "(214) 555-0476", events: 1, hue: 290 },
];

export default function EventTicketingBuyerDb() {
  const { t } = useLang();

  return (
    <section className="relative py-32 px-6 bg-[#0A0814] text-white overflow-hidden">
      <div
        className="absolute bottom-0 right-0 w-[600px] h-[600px] rounded-full pointer-events-none"
        style={{ background: "radial-gradient(circle, rgba(244,114,182,0.15) 0%, transparent 65%)" }}
      />

      <div className="relative max-w-[1200px] mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16 max-w-[760px] mx-auto"
        >
          <div className="text-[11px] font-black tracking-widest uppercase mb-4" style={{ color: "#F472B6" }}>
            {t("Customer ownership", "Dueño del cliente")}
          </div>
          <h2 className="text-4xl md:text-[64px] font-black tracking-[-0.03em] leading-[1.02]">
            {t("Your buyer list. ", "Tu lista de compradores. ")}
            <span className="text-white/30">{t("Yours.", "Tuya.")}</span>
          </h2>
          <p className="text-[16px] font-medium text-white/55 leading-relaxed mt-6">
            {t(
              "Every ticket sold captures the buyer's email and phone. They stay in your buyer list in Redy, ready for next month's show. No marketplace standing between you and your crowd.",
              "Cada ticket vendido captura email y teléfono del comprador. Quedan en tu lista de compradores en Redy, listos para el show del próximo mes. Sin un marketplace entre tú y tu público."
            )}
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8, ease: [0.21, 0.47, 0.32, 0.98] }}
          className="bg-white/[0.04] backdrop-blur-xl border border-white/10 rounded-[28px] p-6 md:p-8 max-w-[820px] mx-auto"
        >
          <div className="flex items-baseline justify-between mb-5">
            <div className="text-[10px] font-black tracking-widest uppercase text-white/45">
              {t("Your buyer database", "Tu base de compradores")}
            </div>
            <div className="flex items-baseline gap-2">
              <div className="text-[28px] font-black tabular-nums text-white">487</div>
              <div className="text-[11px] font-bold" style={{ color: "#F472B6" }}>
                +{t("from NEON RUSH", "desde NEON RUSH")}
              </div>
            </div>
          </div>

          <div className="space-y-2 mb-6">
            {BUYERS.map((b, i) => (
              <motion.div
                key={b.email}
                initial={{ opacity: 0, x: -16 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ duration: 0.45, delay: 0.1 + 0.08 * i }}
                className="bg-white/[0.03] rounded-[14px] p-3.5 border border-white/5 flex items-center gap-4"
              >
                <div
                  className="w-10 h-10 rounded-full flex items-center justify-center text-white font-black text-[13px] shrink-0"
                  style={{ background: `hsl(${b.hue}, 60%, 55%)` }}
                >
                  {b.initial}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[13px] font-black text-white truncate">{b.name}</div>
                  <div className="flex items-center gap-3 text-[11px] text-white/50 mt-0.5">
                    <span className="flex items-center gap-1 min-w-0">
                      <Mail className="w-2.5 h-2.5 shrink-0" />
                      <span className="truncate">{b.email}</span>
                    </span>
                    <span className="hidden sm:flex items-center gap-1 shrink-0">
                      <Phone className="w-2.5 h-2.5" />
                      <span>{b.phone}</span>
                    </span>
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <div className="text-[15px] font-black tabular-nums text-white">{b.events}</div>
                  <div className="text-[9px] font-bold uppercase tracking-wider text-white/40">
                    {t("events", "eventos")}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.6, delay: 0.7 }}
            className="flex items-center justify-between rounded-[16px] px-4 py-3.5 text-white"
            style={{
              background: "linear-gradient(135deg, #7C3AED 0%, #F472B6 100%)",
            }}
          >
            <div className="flex items-center gap-2.5">
              <Send className="w-3.5 h-3.5" />
              <span className="text-[13px] font-bold">
                {t("487 buyers in your list", "487 compradores en tu lista")}
              </span>
            </div>
            <div className="text-[10px] font-black uppercase tracking-wider px-2.5 py-1 rounded-full bg-white/20">
              {t("View", "Ver")}
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
